import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, FileDown } from "lucide-react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";
import { obtenerInfoUsuario } from "../api/api-autenticacion";

const LABEL_BG = "#f1ddcf"; // light peach-ish like template
const BORDER = "#222";
const TITLE_BG = "#ffffff";
const HIGHLIGHT_BG = "#fff59a"; // optional highlight

// Helper: checkbox-like visual
function Box({ checked = false }) {
    return (
        <span
            style={{
                display: "inline-flex",
                width: 14,
                height: 14,
                border: `1px solid ${BORDER}`,
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                lineHeight: 1,
            }}
        >
            {checked ? "✓" : ""}
        </span>
    );
}

function clampStr(v) {
    if (v === null || v === undefined) return "";
    return String(v);
}

export default function ModalFormularioRetornoMercaderia({
    visible = false,
    onClose,
    t = (x) => x,
    // Header info
    nombreChofer = "",
    placaCamion = "",
    fechaRetorno = "", // string "DD/MM/YYYY" or empty
    horaRetorno = "",
    numeroDocumento = "",

    // Table rows
    rows = [],
    selectedDate,
}) {
    const contentRef = useRef(null);
    const [isExporting, setIsExporting] = useState(false);

    const defaultOtrosRows = useMemo(
        () => [
            {
            },
            {
            },
            {
            },
            {
            },
        ],
        []
    );

    const otros = defaultOtrosRows;

    const [infoUsuario, setInfoUsuario] = useState(null);

    useEffect(() => {
        if (!visible) return;
        obtenerDatos();
    }, [visible]);

    const obtenerDatos = async () => {
        try {
            // console.log('obtenerDatos', items);
            const response = await obtenerInfoUsuario();
            // console.log('response', response);
            setInfoUsuario(response);
        } catch (err) {
            console.log('obtenerDatos err', err);
        }
    };

    const handleExportPdf = async () => {
        if (!contentRef.current) return;

        try {
            const canvas = await html2canvas(contentRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff",
            });

            const pdf = new jsPDF("p", "mm", "letter");
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();

            const margin = 8;
            const usableW = pageW - margin * 2;
            const usableH = pageH - margin * 2;

            // Convert usable PDF height into canvas pixels (keeping aspect ratio)
            const imgScale = usableW / canvas.width;          // mm per px *relative*
            const pageCanvasHeightPx = Math.floor(usableH / imgScale);

            let renderedHeightPx = 0;

            while (renderedHeightPx < canvas.height) {
                const sliceHeightPx = Math.min(pageCanvasHeightPx, canvas.height - renderedHeightPx);

                // Create a slice canvas
                const pageCanvas = document.createElement("canvas");
                pageCanvas.width = canvas.width;
                pageCanvas.height = sliceHeightPx;

                const ctx = pageCanvas.getContext("2d");
                ctx.drawImage(
                    canvas,
                    0,
                    renderedHeightPx,              // source y
                    canvas.width,
                    sliceHeightPx,                 // source height
                    0,
                    0,
                    canvas.width,
                    sliceHeightPx                  // dest height
                );

                const imgData = pageCanvas.toDataURL("image/png");

                if (renderedHeightPx > 0) pdf.addPage("p", "mm", "letter");

                const sliceHeightMm = sliceHeightPx * imgScale;

                pdf.addImage(imgData, "PNG", margin, margin, usableW, sliceHeightMm);

                renderedHeightPx += sliceHeightPx;
            }

            pdf.save(`retorno-mercaderia-${moment().format("YYYY-MM-DD-HHmm")}.pdf`);
        } catch (err) {
            console.warn("Export PDF err", err);
        }
    };


    if (!visible) return null;

    // Motivo/Cantidad/Estado/Accion options shown per row (like template)
    const motivoOptions = [
        "No recibió el cliente",
        "Cliente cerrado",
        "Falta de dinero",
        "Error de pedido",
        "Sobrante de ruta",
        "Producto dañado",
        "Rechazo del cliente",
    ];
    const cantidadOptions = ["Pedido completo", "Pedido parcial", "Cajas", "Unidades"];
    const estadoOptions = ["Bueno", "Dañado", "Abierto", "Vencimiento próximo"];
    const accionOptions = ["Reingreso", "Reenvío", "Cambio", "Modificación de nota", "Merma/Baja"];

    // A helper to render option lists in the same “stacked checkbox” style
    function OptionList({ options, selected }) {
        return (
            <div style={{ display: "grid", gap: 6 }}>
                {options.map((opt) => (
                    <div key={opt} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Box checked={clampStr(selected).toLowerCase() === opt.toLowerCase()} />
                        <span>{opt}</span>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-6xl max-h-[90vh] flex flex-col">
                {/* Header bar */}
                <div className="border-b border-gray-200 shrink-0">
                    <div className="mx-auto w-full max-w-6xl px-4 py-3 flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {t("FORMULARIO DE RETORNO DE MERCADERIA")}
                        </h3>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={handleExportPdf}
                                className="flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium"
                            >
                                <FileDown className="h-4 w-4" />
                                {t("Exportar PDF")}
                            </button>

                            <button
                                type="button"
                                onClick={onClose}
                                className="p-1 text-gray-600 hover:text-gray-900 rounded"
                                aria-label={t("Cerrar")}
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </div>


                {/* Content */}
                <div className="flex-1 overflow-auto p-4">
                    <div
                        ref={contentRef}
                        className="bg-white text-black"
                        style={{
                            // width: "215.9mm",
                            // minWidth: "215.9mm",
                            fontFamily: "sans-serif",
                            fontSize: "11px",
                            padding: "8px",
                        }}
                    >
                        {/* Top Title */}
                        <div style={{ textAlign: "center", marginBottom: 8 }}>
                            <div style={{ fontWeight: "bold", fontSize: 18, textDecoration: "underline" }}>
                                FORMULARIO DE RETORNO DE MERCADERIA
                            </div>
                            <div style={{ fontSize: 12, fontWeight: "bold", marginTop: 2 }}>
                                (Control de mercadería no entregada, devuelta o sobrante)
                            </div>
                        </div>

                        {/* Header info table */}
                        <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${BORDER}`, marginBottom: 12 }}>
                            <tbody>
                                <tr>
                                    <td style={{ width: "18%", border: `1px solid ${BORDER}`, padding: "6px 8px", backgroundColor: LABEL_BG, fontWeight: "bold" }}>
                                        EMPRESA:
                                    </td>
                                    <td style={{ width: "32%", border: `1px solid ${BORDER}`, padding: "6px 8px" }}>{infoUsuario?.nombreEmpresa ?? ""}</td>

                                    <td style={{ width: "18%", border: `1px solid ${BORDER}`, padding: "6px 8px", backgroundColor: LABEL_BG, fontWeight: "bold" }}>
                                        NOMBRE DEL CHOFER:
                                    </td>
                                    <td style={{ width: "32%", border: `1px solid ${BORDER}`, padding: "6px 8px", fontWeight: "bold", backgroundColor: TITLE_BG }}>
                                        {nombreChofer}
                                    </td>
                                </tr>

                                <tr>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", backgroundColor: LABEL_BG, fontWeight: "bold" }}>
                                        FECHA DE RETORNO:
                                    </td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px" }}>{fechaRetorno}</td>

                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", backgroundColor: LABEL_BG, fontWeight: "bold" }}>
                                        PLACA DEL CAMION:
                                    </td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", fontWeight: "bold", backgroundColor: TITLE_BG }}>
                                        {placaCamion}
                                    </td>
                                </tr>

                                <tr>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", backgroundColor: LABEL_BG, fontWeight: "bold" }}>
                                        HORA DE RETORNO:
                                    </td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px" }}>{horaRetorno}</td>

                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", backgroundColor: LABEL_BG, fontWeight: "bold" }}>
                                        N° DE DOCUMENTO:
                                    </td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", fontWeight: "bold" }}>
                                        {numeroDocumento}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Main big table */}
                        <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${BORDER}` }}>
                            <thead>
                                <tr>
                                    {[
                                        "N°",
                                        "HOJA DE RUTA (si aplica)",
                                        "CLIENTE",
                                        "N° VENTA",
                                        "N° FACTURA",
                                        "MOTIVO DE RETORNO",
                                        "CANTIDAD/DESCRIPCIÓN",
                                        "ESTADO DEL PRODUCTO",
                                        "ACCIÓN A TOMAR",
                                        "OBSERVACIONES (solo si corresponde)",
                                    ].map((h) => (
                                        <th
                                            key={h}
                                            style={{
                                                border: `1px solid ${BORDER}`,
                                                padding: "6px 6px",
                                                backgroundColor: LABEL_BG,
                                                textAlign: "center",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={10} style={{ border: `1px solid ${BORDER}`, padding: 10, textAlign: "center", color: "#666" }}>
                                            {t("Sin filas para mostrar")}
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((r, idx) => (
                                        <tr key={r.id || idx} style={{ verticalAlign: "top" }}>
                                            <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", textAlign: "center", width: "3%" }}>{idx + 1}</td>

                                            <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", textAlign: "center", width: "10%" }}>
                                                {'RG- LOG- 01-' + moment(selectedDate).format('YYYYMMDD')}
                                            </td>

                                            <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", width: "14%" }}>
                                                <div style={{ fontWeight: "bold" }}>{r.cliente ?? ""}</div>
                                            </td>

                                            <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", textAlign: "center", width: "8%" }}>
                                                {r.numeroVenta ?? ""}
                                            </td>

                                            <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", textAlign: "center", width: "8%" }}>
                                                {r.numeroFactura ?? ""}
                                            </td>

                                            <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", width: "17%" }}>
                                                <OptionList options={motivoOptions} selected={r.motivoRetorno} />
                                            </td>

                                            <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", width: "12%" }}>
                                                <OptionList options={cantidadOptions} selected={r.cantidad} />
                                            </td>

                                            <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", width: "12%" }}>
                                                <OptionList options={estadoOptions} selected={r.estadoProducto} />
                                            </td>

                                            <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", width: "10%" }}>
                                                <OptionList options={accionOptions} selected={r.accionTomar} />
                                            </td>

                                            <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", width: "16%" }}>
                                                {r.observaciones ?? ""}
                                            </td>
                                        </tr>
                                    ))
                                )}

                                {/* OTROS header row */}
                                <tr>
                                    <td colSpan={10} style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", backgroundColor: LABEL_BG, fontWeight: "bold" }}>
                                        OTROS
                                    </td>
                                </tr>

                                {/* OTROS rows */}
                                {otros.map((r, idx) => (
                                    <tr key={r.id || idx} style={{ verticalAlign: "top" }}>
                                        <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", textAlign: "center", width: "3%" }}>{idx + 1}</td>

                                        <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", textAlign: "center", width: "10%" }}>
                                            {r.hojaRuta ?? ""}
                                        </td>

                                        <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", width: "14%" }}>
                                            <div style={{ fontWeight: "bold" }}>{r.cliente ?? ""}</div>
                                        </td>

                                        <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", textAlign: "center", width: "8%" }}>
                                            {r.numeroVenta ?? ""}
                                        </td>

                                        <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", textAlign: "center", width: "8%" }}>
                                            {r.numeroFactura ?? ""}
                                        </td>

                                        <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", width: "17%" }}>
                                            <OptionList options={motivoOptions} selected={r.motivoRetorno} />
                                        </td>

                                        <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", width: "12%" }}>
                                            <OptionList options={cantidadOptions} selected={r.cantidad} />
                                        </td>

                                        <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", width: "12%" }}>
                                            <OptionList options={estadoOptions} selected={r.estadoProducto} />
                                        </td>

                                        <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", width: "10%" }}>
                                            <OptionList options={accionOptions} selected={r.accionTomar} />
                                        </td>

                                        <td style={{ border: `1px solid ${BORDER}`, padding: "8px 6px", width: "16%" }}>
                                            {r.observaciones ?? ""}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Observaciones section */}
                        <div style={{ marginTop: 12 }}>
                            <div style={{ fontWeight: "bold", marginBottom: 6 }}>OBSERVACIONES:</div>
                            <div style={{ lineHeight: 1.4, fontWeight: "bold" }}>
                                *Marcar una sola opción (✓) en motivo de retorno, cantidad/descripción, estado del producto, acción a tomar.
                            </div>
                            <div style={{ lineHeight: 1.4, fontWeight: "bold" }}>
                                *El presente formulario aplica a todo retorno de mercadería, independientemente de la fecha, ruta o medio de transporte original.
                            </div>
                        </div>

                        {/* Responsabilidad y conformidad */}
                        <div
                            style={{
                                marginTop: 10,
                                border: `1px solid ${BORDER}`,
                                backgroundColor: LABEL_BG,
                                padding: "8px 10px",
                            }}
                        >
                            <div style={{ fontWeight: "bold", textDecoration: "underline", marginBottom: 6 }}>
                                RESPONSABILIDAD Y CONFORMIDAD
                            </div>
                            <div style={{ lineHeight: 1.4 }}>
                                Declaro haber recibido y entregado la mercadería correspondiente a los documentos de venta y/o facturación referenciados en el presente documento,
                                siendo responsable de su correcta disposición.
                            </div>
                        </div>

                        {/* Signature block (3 columns like image) */}
                        <div style={{ display: "flex", gap: 24, marginTop: 18 }}>
                            {[
                                { title: "CHOFER", withDate: false },
                                { title: "RESPONSABLE DE ALMACÉN", withDate: false },
                                { title: "VERIFICADO", withDate: true },
                            ].map((b) => (
                                <div key={b.title} style={{ flex: 1 }}>
                                    <div style={{ fontWeight: "bold", marginBottom: 10 }}>{b.title}</div>
                                    <div style={{ marginBottom: 10 }}>
                                        <span style={{ fontWeight: "bold" }}>Nombre:</span> ____________________________
                                    </div>
                                    <div style={{ marginBottom: 10 }}>
                                        <span style={{ fontWeight: "bold" }}>Firma:</span> _____________________________
                                    </div>
                                    {b.withDate && (
                                        <div>
                                            <span style={{ fontWeight: "bold" }}>Fecha:</span> _____________________________
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer close */}
                <div className="px-4 py-3 border-t border-gray-200 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-sm font-medium"
                    >
                        {t("Cerrar")}
                    </button>
                </div>
            </div>
        </div>
    );
}
